import {
  accounts,
  invites,
  loginAttempts,
  passwordResetLinks,
  sessions,
  users,
  verifications,
} from './schema/accounts';
import {
  budgetSettings,
  expectedMoney,
  expectedReceipts,
  funds,
  incomeSources,
  moneyAccounts,
  paydays,
  receivables,
} from './schema/budget';
import { ledgerEntries } from './schema/ledger';
import { installments, payees, paymentDues, payments, paymentsSettings } from './schema/payments';
import { cravings, quitPlans, smokeLogs } from './schema/quit';
import { shoppingItems, shoppingTripItems, shoppingTrips } from './schema/shopping';
import { categories } from './schema/spending';
import { changes, outbox, syncState } from './schema/sync';
import { weightEntries, weightSettings } from './schema/weight';
import {
  appSettings,
  auditLog,
  history,
  moduleOverrides,
  moduleStates,
  tiles,
  userSettings,
} from './schema/system';

export * from './schema/common';
export * from './schema/accounts';
export * from './schema/system';
export * from './schema/sync';
export * from './schema/spending';
export * from './schema/ledger';
export * from './schema/budget';
export * from './schema/shopping';
export * from './schema/quit';
export * from './schema/payments';
export * from './schema/weight';

/** Every table, keyed by its SQL name. Add new tables here (a test checks this against the DB). */
export const TABLES = {
  users,
  sessions,
  accounts,
  verifications,
  invites,
  password_reset_links: passwordResetLinks,
  login_attempts: loginAttempts,
  app_settings: appSettings,
  module_states: moduleStates,
  module_overrides: moduleOverrides,
  audit_log: auditLog,
  history,
  changes,
  outbox,
  sync_state: syncState,
  tiles,
  categories,
  user_settings: userSettings,
  ledger_entries: ledgerEntries,
  funds,
  budget_settings: budgetSettings,
  income_sources: incomeSources,
  paydays,
  shopping_items: shoppingItems,
  shopping_trips: shoppingTrips,
  shopping_trip_items: shoppingTripItems,
  quit_plans: quitPlans,
  smoke_logs: smokeLogs,
  cravings,
  payees,
  payment_dues: paymentDues,
  payments,
  installments,
  money_accounts: moneyAccounts,
  expected_money: expectedMoney,
  receivables,
  weight_entries: weightEntries,
  weight_settings: weightSettings,
  expected_receipts: expectedReceipts,
  payments_settings: paymentsSettings,
} as const;

export type TableName = keyof typeof TABLES;

/** User-owned tables that travel as mutations through /api/sync push and pull. */
export const SYNCED_TABLES = [
  'module_states',
  'tiles',
  'categories',
  'user_settings',
  'ledger_entries',
  'funds',
  'budget_settings',
  'income_sources',
  'paydays',
  'shopping_items',
  'shopping_trips',
  'shopping_trip_items',
  'quit_plans',
  'smoke_logs',
  'cravings',
  'payees',
  'payment_dues',
  'payments',
  'installments',
  'money_accounts',
  'expected_money',
  'receivables',
  'weight_entries',
  'weight_settings',
  'expected_receipts',
  'payments_settings',
] as const satisfies readonly TableName[];

export type SyncedTableName = (typeof SYNCED_TABLES)[number];

/** Live only in D1; reach a device only through dedicated endpoints, never through sync. */
export const SERVER_ONLY_TABLES = [
  'users',
  'sessions',
  'accounts',
  'verifications',
  'invites',
  'password_reset_links',
  'login_attempts',
  'app_settings',
  'module_overrides',
  'audit_log',
  'changes',
] as const satisfies readonly TableName[];

/** Used only in the device database (they exist, empty, in D1 because migrations are shared). */
export const DEVICE_ONLY_TABLES = ['outbox', 'sync_state'] as const satisfies readonly TableName[];

/** Written separately on each side while applying mutations; never sent as a mutation. */
export const LOCAL_LOG_TABLES = ['history'] as const satisfies readonly TableName[];

export function isSyncedTable(name: string): name is SyncedTableName {
  return (SYNCED_TABLES as readonly string[]).includes(name);
}
